import React, { useEffect, useRef } from 'react';
import './DitheringBackground.css';

// Animated film-grain style background
// - Renders a low-res noise texture and tiles it across the viewport
// - Updates at a capped FPS for subtle motion
// - Intensity controls contrast of the noise
const DitheringBackground = ({ fps = 12, intensity = 0.35, noiseSize = 128, scale = 2 }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const lastFrameTimeRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Ensure full-viewport canvas
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Offscreen noise canvas (low resolution)
        const noiseCanvas = document.createElement('canvas');
        const noiseCtx = noiseCanvas.getContext('2d', { willReadFrequently: true });
        noiseCanvas.width = noiseSize;
        noiseCanvas.height = noiseSize;

        // Scaled pattern canvas (to enlarge grain size)
        const patternCanvas = document.createElement('canvas');
        const patternCtx = patternCanvas.getContext('2d');

        const clamp01 = (v) => Math.max(0, Math.min(1, v));
        const frameIntervalMs = 1000 / Math.max(1, fps);
        const noiseIntensity = clamp01(intensity); // 0..1
        const patternScale = Math.max(1, Math.floor(scale));

        const updatePatternCanvasSize = () => {
            patternCanvas.width = noiseCanvas.width * patternScale;
            patternCanvas.height = noiseCanvas.height * patternScale;
        };
        updatePatternCanvasSize();

        // Create a new noise frame into the offscreen canvas
        const renderNoise = () => {
            const imageData = noiseCtx.createImageData(noiseSize, noiseSize);
            const data = imageData.data;

            // Generate monochrome noise
            const spread = Math.floor(255 * 0.5 * noiseIntensity);
            const base = 127;

            for (let i = 0; i < data.length; i += 4) {
                const val = base + (Math.random() * 2 - 1) * spread;
                const v = Math.max(0, Math.min(255, val | 0));
                data[i] = v;
                data[i + 1] = v;
                data[i + 2] = v;
                data[i + 3] = 255;
            }

            noiseCtx.putImageData(imageData, 0, 0);

            // Build scaled pattern canvas
            patternCtx.imageSmoothingEnabled = false;
            patternCtx.clearRect(0, 0, patternCanvas.width, patternCanvas.height);
            patternCtx.drawImage(
                noiseCanvas,
                0,
                0,
                noiseCanvas.width,
                noiseCanvas.height,
                0,
                0,
                patternCanvas.width,
                patternCanvas.height
            );
        };

        // Main draw: tile scaled pattern across viewport
        const draw = () => {
            const pattern = ctx.createPattern(patternCanvas, 'repeat');
            if (!pattern) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            // Slight random offset each frame to avoid static look
            const offsetX = Math.random() * patternCanvas.width * 0.5;
            const offsetY = Math.random() * patternCanvas.height * 0.5;
            ctx.translate(-offsetX, -offsetY);
            ctx.imageSmoothingEnabled = false;
            ctx.fillStyle = pattern;
            ctx.fillRect(0, 0, canvas.width + offsetX, canvas.height + offsetY);
            ctx.restore();
        };

        // Animation loop with FPS cap
        const animate = (timestamp) => {
            if (!lastFrameTimeRef.current) lastFrameTimeRef.current = timestamp;
            const delta = timestamp - lastFrameTimeRef.current;
            if (delta >= frameIntervalMs) {
                lastFrameTimeRef.current = timestamp;
                renderNoise();
                draw();
            }
            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [fps, intensity, noiseSize, scale]);

    return (
        <canvas
            ref={canvasRef}
            className="dithering-background"
        />
    );
};

export default DitheringBackground; 